import { useAuthStore } from '../stores/auth.store';
import { extractErrorMessage, handleErrorToast, handleSuccessToast, NON_RECOVERABLE_STATUSES } from './http-utils';

// Default configurations
const DEFAULT_INTERCEPTOR_CONFIG = {
    nonRecoverableStatuses: NON_RECOVERABLE_STATUSES,
    showSuccessToasts: true,
    logoutUrl: '/account/logout',
    redirectDelay: 150
};

const DEFAULT_AUTH_CONFIG = {
    headerName: 'Authorization',
    tokenPrefix: 'Bearer '
};

/**
 * Creates a reusable authentication request interceptor
 * Automatically adds Bearer token to requests when available
 *
 * @param {Object} options - Configuration options
 * @param {string} options.headerName - Name of the authorization header (default: 'Authorization')
 * @param {string} options.tokenPrefix - Token prefix (default: 'Bearer ')
 *
 * @returns {Function} Request interceptor function
 *
 * @example
 * const authInterceptor = createAuthRequestInterceptor();
 * axiosInstance.interceptors.request.use(authInterceptor, handleRequestError);
 */
export function createAuthRequestInterceptor(options = {}) {
    const { headerName = DEFAULT_AUTH_CONFIG.headerName, tokenPrefix = DEFAULT_AUTH_CONFIG.tokenPrefix } = options;

    return (config) => {
        const authStore = useAuthStore();

        if (authStore.accessToken) {
            config.headers[headerName] = `${tokenPrefix}${authStore.accessToken}`;
        }

        return config;
    };
}

/**
 * Standard request error handler
 * Can be extended for custom request error logic
 *
 * @param {Object} error - Request error object
 * @returns {Promise} Rejected promise with error
 */
export function handleRequestError(error) {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
}

/**
 * Creates a response success interceptor that handles both success messages and embedded errors
 * Supports extensible error detection and toast configuration
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.nonRecoverableStatuses - Status codes that should always show toasts
 * @param {boolean} options.showSuccessToasts - Whether to show success toasts (default: true)
 *
 * @returns {Function} Response success interceptor function
 */
export function createResponseSuccessInterceptor(options = {}) {
    const { nonRecoverableStatuses = NON_RECOVERABLE_STATUSES, showSuccessToasts = DEFAULT_INTERCEPTOR_CONFIG.showSuccessToasts } = options;

    return (response) => {
        // Extract potential error from successful response
        // Exclude data.message and data.detail since they might contain success messages
        const errorMessage = extractErrorMessage(response, ['data.message', 'data.detail']);
        const hideToast = !!response.config.hideToast;

        // Handle embedded errors in successful responses
        if (errorMessage) {
            const shouldHide = hideToast && !nonRecoverableStatuses.includes(response.status);

            if (!shouldHide) {
                handleErrorToast(errorMessage, false, response.status);
            }

            throw new Error(errorMessage);
        }

        // Handle success messages
        if (showSuccessToasts && response.data?.message && !hideToast) {
            handleSuccessToast(response.data.message);
        }

        return response;
    };
}

/**
 * Creates a response error interceptor with extensible error handling
 * Supports custom logout handling, error message extraction, and toast configuration
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.nonRecoverableStatuses - Status codes that should always show toasts
 * @param {Function} options.onUnauthorized - Custom 401 handler (default: logout redirect)
 * @param {Function} options.onLogout - Custom logout handler (default: logout redirect)
 * @param {string} options.logoutUrl - Logout redirect URL (default: '/account/logout')
 * @param {number} options.redirectDelay - Delay before redirect in milliseconds (default: 150)
 *
 * @returns {Function} Response error interceptor function
 */
export function createResponseErrorInterceptor(options = {}) {
    const {
        nonRecoverableStatuses = NON_RECOVERABLE_STATUSES,
        onUnauthorized,
        onLogout,
        logoutUrl = DEFAULT_INTERCEPTOR_CONFIG.logoutUrl,
        redirectDelay = DEFAULT_INTERCEPTOR_CONFIG.redirectDelay
    } = options;

    return (error) => {
        const status = error.response?.status || error.status;
        const hideToast = !!error?.config?.hideToast;

        // Handle 401 unauthorized errors
        if (status === 401) {
            if (onUnauthorized) {
                onUnauthorized(error);
            } else {
                setTimeout(() => (window.location.href = logoutUrl), redirectDelay);
            }

            return Promise.reject(error);
        }

        // Handle explicit logout flag
        if (error.response?.data?.logout) {
            if (onLogout) {
                onLogout(error);
            } else {
                setTimeout(() => (window.location.href = logoutUrl), redirectDelay);
            }
        }

        // Extract and display error message
        const responseData = error.response || error?.message || error.data || {};
        const errorMessage = extractErrorMessage(responseData);

        if (errorMessage) {
            const shouldHide = hideToast && !nonRecoverableStatuses.includes(status);

            if (!shouldHide) {
                handleErrorToast(errorMessage, false, status);
            }
        }

        throw error;
    };
}
